/**
 * Custom Calendar Picker Component
 * A reusable date/date-range picker with visual calendar interface
 * Used for date-only selections (not datetime)
 */

/**
 * Format a Date to YYYY-MM-DD using local time (not UTC).
 * Using toISOString() converts to UTC first, which shifts the date backwards
 * for users in UTC+ timezones (e.g. UTC+2 midnight becomes the previous day in UTC).
 */
function formatLocalDate(d) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Create a calendar picker instance
 * @param {Object} options - Configuration options
 * @param {string} options.inputId - ID of the hidden input element
 * @param {string} options.buttonId - ID of the trigger button
 * @param {string} options.displayId - ID of the display element (optional)
 * @param {string} options.popupId - ID of the calendar popup container
 * @param {boolean} options.isRange - Whether to select a date range (default: false)
 * @param {Function} options.onChange - Callback when date(s) change
 * @param {Date} options.defaultDate - Default date to show (default: today)
 */
export function createCalendarPicker(options) {
    const {
        inputId,
        buttonId,
        displayId,
        popupId,
        isRange = false,
        onChange,
        defaultDate = new Date()
    } = options;

    const state = {
        isOpen: false,
        currentMonth: new Date(defaultDate),
        selectedDate: isRange ? null : new Date(defaultDate),
        startDate: null,
        endDate: null,
        isRange
    };
    
    // Initialize from input values if they exist
    if (isRange) {
        const fromInput = document.getElementById(inputId);
        const toInput = document.getElementById(inputId.replace('-from', '-to'));
        
        if (fromInput?.value) {
            state.startDate = new Date(fromInput.value);
            state.startDate.setHours(0, 0, 0, 0);
        } else {
            state.startDate = new Date(defaultDate);
            state.startDate.setHours(0, 0, 0, 0);
        }
        
        if (toInput?.value) {
            state.endDate = new Date(toInput.value);
            state.endDate.setHours(0, 0, 0, 0);
        } else {
            state.endDate = new Date();
            state.endDate.setHours(0, 0, 0, 0);
        }
        
        state.currentMonth = new Date(state.startDate);
    } else {
        state.currentMonth.setHours(0, 0, 0, 0);
        if (state.selectedDate) state.selectedDate.setHours(0, 0, 0, 0);
    }

    // Set default values
    if (!isRange) {
        state.currentMonth.setHours(0, 0, 0, 0);
        if (state.selectedDate) state.selectedDate.setHours(0, 0, 0, 0);
    }

    /**
     * Toggle calendar popup
     */
    function toggle() {
        const popup = document.getElementById(popupId);
        if (!popup) return;

        if (state.isOpen) {
            // Close with animation
            popup.style.opacity = '0';
            popup.style.transform = 'translateY(-8px)';
            setTimeout(() => {
                popup.classList.add('hidden');
                popup.style.opacity = '';
                popup.style.transform = '';
            }, 150);
            state.isOpen = false;
            return;
        }

        // Load current values from input
        if (isRange) {
            const fromInput = document.getElementById(inputId);
            const toInput = document.getElementById(inputId.replace('-from', '-to'));
            
            if (fromInput?.value) {
                state.startDate = new Date(fromInput.value);
                state.startDate.setHours(0, 0, 0, 0);
            }
            if (toInput?.value) {
                state.endDate = new Date(toInput.value);
                state.endDate.setHours(0, 0, 0, 0);
            }

            // Set calendar to end date month (newest) so the user sees today's date by default
            if (state.endDate) {
                state.currentMonth = new Date(state.endDate);
            } else if (state.startDate) {
                state.currentMonth = new Date(state.startDate);
            } else {
                state.currentMonth = new Date();
            }
        } else {
            const input = document.getElementById(inputId);
            if (input?.value) {
                state.selectedDate = new Date(input.value);
                state.selectedDate.setHours(0, 0, 0, 0);
                state.currentMonth = new Date(state.selectedDate);
            }
        }

        // Render and show calendar
        state.isOpen = true;
        render();

        // Position popup relative to button
        const button = document.getElementById(buttonId);
        if (button) {
            const rect = button.getBoundingClientRect();
            popup.style.left = `${rect.left}px`;
            popup.style.top = `${rect.bottom + 4}px`;
        }

        // Open with fade-in and slide-down animation
        popup.style.opacity = '0';
        popup.style.transform = 'translateY(-8px)';
        popup.style.transition = 'opacity 150ms ease-out, transform 150ms ease-out';
        popup.classList.remove('hidden');

        // Trigger animation on next frame
        requestAnimationFrame(() => {
            popup.style.opacity = '1';
            popup.style.transform = 'translateY(0)';
        });
    }

    /**
     * Update display text
     */
    function updateDisplay() {
        if (!displayId) return;
        
        const display = document.getElementById(displayId);
        if (!display) return;

        const formatDate = (d) => {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
        };

        if (isRange) {
            if (state.startDate && state.endDate) {
                display.textContent = `${formatDate(state.startDate)} - ${formatDate(state.endDate)}`;
                display.classList.remove('text-gray-400');
                display.classList.add('text-gray-700');
            } else if (state.startDate) {
                display.textContent = `${formatDate(state.startDate)} - Select end date`;
                display.classList.remove('text-gray-400');
                display.classList.add('text-gray-700');
            } else {
                display.textContent = 'Select date range';
                display.classList.remove('text-gray-700');
                display.classList.add('text-gray-400');
            }
        } else {
            if (state.selectedDate) {
                display.textContent = formatDate(state.selectedDate);
                display.classList.remove('text-gray-400');
                display.classList.add('text-gray-700');
            } else {
                display.textContent = 'Select date';
                display.classList.remove('text-gray-700');
                display.classList.add('text-gray-400');
            }
        }
    }

    /**
     * Render calendar
     */
    function render() {
        const popup = document.getElementById(popupId);
        if (!popup) return;

        const currentDate = state.currentMonth;
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        // Get first day of month and number of days
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startDayOfWeek = firstDay.getDay(); // 0 = Sunday
        const adjustedStartDay = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1; // Adjust to Monday=0

        // Get previous month's last days
        const prevMonth = new Date(year, month, 0);
        const daysInPrevMonth = prevMonth.getDate();

        // Get today's date for highlighting
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Month names
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                           'July', 'August', 'September', 'October', 'November', 'December'];

        // Build calendar HTML
        let html = `
            <div class="flex items-center text-gray-900 mb-4">
                <button type="button" 
                        onclick="window.calendarPickers['${popupId}'].changeMonth(-1); event.stopPropagation();"
                        class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500">
                    <span class="sr-only">Previous month</span>
                    <svg viewBox="0 0 20 20" fill="currentColor" class="size-5">
                        <path d="M11.78 5.22a.75.75 0 010 1.06L8.06 10l3.72 3.72a.75.75 0 11-1.06 1.06l-4.25-4.25a.75.75 0 010-1.06l4.25-4.25a.75.75 0 011.06 0z" clip-rule="evenodd" fill-rule="evenodd"/>
                    </svg>
                </button>
                <div class="flex-auto text-sm font-semibold text-center">${monthNames[month]} ${year}</div>
                <button type="button" 
                        onclick="window.calendarPickers['${popupId}'].changeMonth(1); event.stopPropagation();"
                        class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500">
                    <span class="sr-only">Next month</span>
                    <svg viewBox="0 0 20 20" fill="currentColor" class="size-5">
                        <path d="M8.22 5.22a.75.75 0 011.06 0l4.25 4.25a.75.75 0 010 1.06l-4.25 4.25a.75.75 0 01-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 010-1.06z" clip-rule="evenodd" fill-rule="evenodd"/>
                    </svg>
                </button>
            </div>
            
            <!-- Weekday headers -->
            <div class="grid grid-cols-7 text-xs text-center font-semibold text-gray-500 mb-2">
                <div>M</div><div>T</div><div>W</div><div>T</div><div>F</div><div>S</div><div>S</div>
            </div>
            
            <!-- Calendar grid -->
            <div class="grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200">
        `;

        // Helper functions
        const isInRange = (dateObj) => {
            if (!state.startDate || !state.endDate) return false;
            return dateObj >= state.startDate && dateObj <= state.endDate;
        };

        const isRangeStart = (dateObj) => {
            return state.startDate && dateObj.getTime() === state.startDate.getTime();
        };

        const isRangeEnd = (dateObj) => {
            return state.endDate && dateObj.getTime() === state.endDate.getTime();
        };

        const isSelected = (dateObj) => {
            return state.selectedDate && dateObj.getTime() === state.selectedDate.getTime();
        };

        // Previous month's trailing days
        for (let i = adjustedStartDay - 1; i >= 0; i--) {
            const day = daysInPrevMonth - i;
            html += `
                <button type="button" 
                        class="py-1.5 bg-gray-50 text-gray-400 hover:bg-gray-100 transition-colors duration-150"
                        onclick="window.calendarPickers['${popupId}'].selectDate(${year}, ${month - 1}, ${day}); event.stopPropagation();">
                    ${day}
                </button>
            `;
        }

        // Current month's days
        for (let day = 1; day <= daysInMonth; day++) {
            const currentDateObj = new Date(year, month, day);
            currentDateObj.setHours(0, 0, 0, 0);

            const isToday = currentDateObj.getTime() === today.getTime();
            let classes = 'py-1.5 bg-white hover:bg-gray-100 transition-colors duration-150';
            let textClasses = '';

            if (isRange) {
                const isStart = isRangeStart(currentDateObj);
                const isEnd = isRangeEnd(currentDateObj);
                const inRange = isInRange(currentDateObj);

                if (isStart || isEnd) {
                    classes = 'py-1.5 font-semibold text-white bg-primary-600 hover:bg-primary-500 transition-colors duration-150';
                } else if (inRange) {
                    classes = 'py-1.5 bg-primary-100 hover:bg-primary-200 text-primary-900 transition-colors duration-150';
                } else if (isToday) {
                    classes += ' font-semibold';
                    textClasses = ' text-primary-600';
                }
            } else {
                const selected = isSelected(currentDateObj);
                if (selected) {
                    classes = 'py-1.5 font-semibold text-white bg-primary-600 hover:bg-primary-500 transition-colors duration-150';
                } else if (isToday) {
                    classes += ' font-semibold';
                    textClasses = ' text-primary-600';
                }
            }

            html += `
                <button type="button" 
                        class="${classes}${textClasses}"
                        onclick="window.calendarPickers['${popupId}'].selectDate(${year}, ${month}, ${day}); event.stopPropagation();">
                    ${day}
                </button>
            `;
        }

        // Next month's leading days
        const totalCells = adjustedStartDay + daysInMonth;
        const remainingCells = 42 - totalCells; // 6 rows of 7 days
        for (let day = 1; day <= remainingCells && day <= 14; day++) {
            html += `
                <button type="button" 
                        class="py-1.5 bg-gray-50 text-gray-400 hover:bg-gray-100 transition-colors duration-150"
                        onclick="window.calendarPickers['${popupId}'].selectDate(${year}, ${month + 1}, ${day}); event.stopPropagation();">
                    ${day}
                </button>
            `;
        }

        html += `
            </div>
            <div class="mt-3 text-xs text-center text-gray-500 mb-2">
                ${isRange 
                    ? (state.startDate && !state.endDate ? 'Select end date' : 'Click to select start date, then end date')
                    : 'Click to select date'
                }
            </div>
            <div class="flex gap-2">
                <button type="button" 
                        onclick="window.calendarPickers['${popupId}'].clear(); event.stopPropagation();"
                        class="flex-1 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors duration-150">
                    Clear
                </button>
                <button type="button" 
                        onclick="window.calendarPickers['${popupId}'].setToday(); event.stopPropagation();"
                        class="flex-1 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors duration-150">
                    Today
                </button>
                ${isRange ? `
                <button type="button" 
                        onclick="window.calendarPickers['${popupId}'].apply(); event.stopPropagation();"
                        class="flex-1 rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 transition-colors duration-150">
                    Apply
                </button>
                ` : ''}
            </div>
        `;

        popup.innerHTML = html;
    }

    /**
     * Select a date
     */
    function selectDate(year, month, day) {
        const selectedDateObj = new Date(year, month, day);
        selectedDateObj.setHours(0, 0, 0, 0);

        if (isRange) {
            // Range selection logic
            if (!state.startDate || (state.startDate && state.endDate)) {
                // Start new range
                state.startDate = selectedDateObj;
                state.endDate = null;
            } else if (selectedDateObj < state.startDate) {
                // Selected before start, make it the new start
                state.endDate = state.startDate;
                state.startDate = selectedDateObj;
            } else {
                // Selected after start, make it the end
                state.endDate = selectedDateObj;
            }

            render();
            updateDisplay();
        } else {
            // Single date selection
            state.selectedDate = selectedDateObj;
            
            // Update input
            const input = document.getElementById(inputId);
            if (input) {
                input.value = formatLocalDate(state.selectedDate);
            }

            updateDisplay();
            
            // Call onChange callback
            if (onChange) {
                onChange(state.selectedDate);
            }

            // Close calendar after selection
            toggle();
        }
    }

    /**
     * Change month
     */
    function changeMonth(delta) {
        state.currentMonth.setMonth(state.currentMonth.getMonth() + delta);
        render();
    }

    /**
     * Clear selection
     */
    function clear() {
        if (isRange) {
            state.startDate = null;
            state.endDate = null;

            // Clear inputs
            const fromInput = document.getElementById(inputId);
            const toInput = document.getElementById(inputId.replace('-from', '-to'));
            if (fromInput) fromInput.value = '';
            if (toInput) toInput.value = '';
        } else {
            state.selectedDate = null;

            // Clear input
            const input = document.getElementById(inputId);
            if (input) input.value = '';
        }

        render();
        updateDisplay();

        if (onChange) {
            onChange(isRange ? { startDate: null, endDate: null } : null);
        }
    }

    /**
     * Set to today
     */
    function setToday() {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (isRange) {
            state.startDate = new Date(today);
            state.endDate = new Date(today);
            state.currentMonth = new Date(today);
        } else {
            state.selectedDate = new Date(today);
            state.currentMonth = new Date(today);

            // Update input
            const input = document.getElementById(inputId);
            if (input) {
                input.value = formatLocalDate(state.selectedDate);
            }
        }

        render();
        updateDisplay();

        if (!isRange) {
            if (onChange) {
                onChange(state.selectedDate);
            }
            toggle();
        }
    }

    /**
     * Apply range selection (close and trigger onChange)
     */
    function apply() {
        console.log('Calendar apply() called', { isRange, startDate: state.startDate, endDate: state.endDate });
        if (!isRange) return;

        // Update inputs
        const fromInput = document.getElementById(inputId);
        const toInput = document.getElementById(inputId.replace('-from', '-to'));
        
        if (fromInput && state.startDate) {
            fromInput.value = formatLocalDate(state.startDate);
        }
        if (toInput && state.endDate) {
            toInput.value = formatLocalDate(state.endDate);
        }

        console.log('Calling onChange callback with dates:', { startDate: state.startDate, endDate: state.endDate });
        // Call onChange callback
        if (onChange) {
            onChange({ startDate: state.startDate, endDate: state.endDate });
        } else {
            console.warn('No onChange callback defined');
        }

        // Close calendar
        toggle();
    }

    // Setup button click handler
    const button = document.getElementById(buttonId);
    if (button) {
        button.addEventListener('click', (e) => {
            e.stopPropagation();
            toggle();
        });
    }

    // Close on outside click
    document.addEventListener('click', (e) => {
        const popup = document.getElementById(popupId);
        if (state.isOpen && popup && !popup.contains(e.target) && e.target.id !== buttonId) {
            toggle();
        }
    });

    // Initialize display
    updateDisplay();

    // Programmatically set the range and update inputs/display. Used by
    // navigation steppers (e.g. Time Budget prev/next arrows) so the picker's
    // internal state stays in sync with the hidden inputs.
    function setRange(startDate, endDate) {
        const fromInput = document.getElementById(inputId);
        const toInput = isRange ? document.getElementById(inputId.replace('-from', '-to')) : null;

        if (startDate) {
            const s = new Date(startDate);
            s.setHours(0, 0, 0, 0);
            state.startDate = s;
            state.currentMonth = new Date(s);
        } else {
            state.startDate = null;
        }
        if (isRange) {
            if (endDate) {
                const e = new Date(endDate);
                e.setHours(0, 0, 0, 0);
                state.endDate = e;
            } else {
                state.endDate = null;
            }
        }

        const toIsoDate = (d) => {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        };
        if (fromInput) fromInput.value = state.startDate ? toIsoDate(state.startDate) : '';
        if (toInput) toInput.value = state.endDate ? toIsoDate(state.endDate) : '';

        updateDisplay();
        if (state.isOpen) render();
    }

    // Programmatically set the single selected date and update the input
    // and display without firing onChange or toggling the popup. Used by
    // navigation steppers (e.g. Time Budget prev/next arrows).
    function setDate(date) {
        if (isRange) return;
        if (!date) {
            state.selectedDate = null;
        } else {
            const d = new Date(date);
            d.setHours(0, 0, 0, 0);
            state.selectedDate = d;
            state.currentMonth = new Date(d);
            const input = document.getElementById(inputId);
            if (input) input.value = formatLocalDate(d);
        }
        updateDisplay();
        if (state.isOpen) render();
    }

    // Return public API
    return {
        toggle,
        selectDate,
        changeMonth,
        clear,
        setToday,
        apply,
        setRange,
        setDate,
        updateDisplay,
        render,
        getState: () => ({ ...state })
    };
}

/**
 * Global calendar pickers registry
 */
if (!window.calendarPickers) {
    window.calendarPickers = {};
}
